import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DISPLAY_NAME_HINT, DISPLAY_NAME_PATTERN } from '@ready/shared';
import { useApi, type Me } from '@/lib/api';
import { Button, ErrorText, Field, Input } from './ui';

/** The rule, in one sentence, for the learner's current situation. */
export function nameRuleText(me: Me): string {
  if (me.name.locked)
    return 'Your name is locked because it appears on certificates. Contact Nirvik support to change it.';
  if (me.name.certificates > 0)
    return 'This name is on your certificates. You can correct it once; after that, only Nirvik support can change it.';
  return 'Use your full name as it should appear on certificates. Once you earn a certificate, you can correct it only once.';
}

/** Edits the name printed on certificates, following the change rules. */
export function NameForm({
  me,
  submitLabel = 'Save',
  onSaved,
}: {
  me: Me;
  submitLabel?: string;
  onSaved?: () => void;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [name, setName] = useState(me.displayName ?? '');
  useEffect(() => setName(me.displayName ?? ''), [me.displayName]);
  const save = useMutation({
    mutationFn: () => api.updateMe(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
      onSaved?.();
    },
  });
  const changingCertified = me.name.certificates > 0 && name.trim() !== (me.displayName ?? '');

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (
          changingCertified &&
          !window.confirm(
            'This is your one name correction. It also updates your existing certificates, and after it only Nirvik support can change your name. Continue?',
          )
        )
          return;
        save.mutate();
      }}
    >
      <Field label="Full name" htmlFor="display-name" hint={nameRuleText(me)}>
        <Input
          id="display-name"
          required
          minLength={2}
          maxLength={100}
          autoComplete="name"
          pattern={DISPLAY_NAME_PATTERN}
          title={DISPLAY_NAME_HINT}
          disabled={me.name.locked}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      {!me.name.locked && (
        <Button
          type="submit"
          disabled={save.isPending || !name.trim() || name.trim() === (me.displayName ?? '')}
        >
          {submitLabel}
        </Button>
      )}
      <ErrorText error={save.error} />
    </form>
  );
}
